
import { html_beautify, HTMLBeautifyOptions } from "js-beautify";

// CodeMirror Imports
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, dropCursor, rectangularSelection, crosshairCursor, highlightActiveLine, ViewUpdate } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { bracketMatching, indentOnInput, syntaxHighlighting, defaultHighlightStyle, foldGutter, foldKeymap } from "@codemirror/language";
import { closeBrackets, closeBracketsKeymap, autocompletion, completionKeymap } from "@codemirror/autocomplete";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { lintKeymap } from "@codemirror/lint";

// Language Support
import { html } from "@codemirror/lang-html";

// Theme
import { vscodeDark } from "@uiw/codemirror-theme-vscode";

const basicSetup = [
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  foldGutter(),
  drawSelection(),
  dropCursor(),
  EditorState.allowMultipleSelections.of(true),
  indentOnInput(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  bracketMatching(),
  closeBrackets(),
  autocompletion(),
  rectangularSelection(),
  crosshairCursor(),
  highlightActiveLine(),
  highlightSelectionMatches(),
  EditorView.lineWrapping,
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...searchKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...completionKeymap,
    ...lintKeymap
  ])
];

export interface EditorManagerOptions {
    code?: string | null;
    storageKey?: string | null;
    resetOnLoad?: boolean;
    container: HTMLElement;
    onUpdate: (code: string) => void;
}

export class EditorManager {
    editorView: EditorView | null = null;
    options: EditorManagerOptions;

    private _debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private _disposed: boolean = false;

    constructor(options: EditorManagerOptions) {
        this.options = options;
    }

    // Initialization precedence (F04/F08):
    // 1. If resetOnLoad is requested, the supplied initial code (or the demo) is used.
    // 2. Otherwise a saved draft wins over the initial code, so reloading a page
    //    never overwrites user edits with the template.
    // 3. Explicit empty strings are preserved; the demo is only used when no
    //    source of content exists at all.
    resolveInitialCode(): string {
        if (this.options.resetOnLoad) {
            return this.options.code !== null && this.options.code !== undefined
                ? this.options.code
                : this._getDefaultInitialCode();
        }

        let savedCode: string | null = null;
        if (this.options.storageKey) {
            try {
                savedCode = localStorage.getItem(this.options.storageKey);
            } catch (e: any) {
                console.warn("SWO: Unable to read from localStorage:", e.message);
            }
        }
        if (savedCode !== null) return savedCode;

        if (this.options.code !== null && this.options.code !== undefined) return this.options.code;

        return this._getDefaultInitialCode();
    }

    // Creates the editor directly from the final initial document. This does
    // not dispatch any transaction, so initialization neither saves to
    // storage nor schedules a preview update (F04/F09).
    init(initialCode: string) {
        const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
            if (this._disposed) return;
            if (update.docChanged) {
                const code = update.state.doc.toString();
                this._saveCode(code);
                if (this._debounceTimeout) clearTimeout(this._debounceTimeout);
                this._debounceTimeout = setTimeout(() => {
                    this._debounceTimeout = null;
                    if (!this._disposed) this.options.onUpdate(code);
                }, 300);
            }
        });

        const startState = EditorState.create({
            doc: initialCode,
            extensions: [
                basicSetup,
                html({ autoCloseTags: true, matchClosingTags: true }),
                vscodeDark,
                updateListener
            ]
        });

        this.editorView = new EditorView({
            state: startState,
            parent: this.options.container
        });
    }

    getCode(): string {
        return this.editorView ? this.editorView.state.doc.toString() : "";
    }

    cancelPendingUpdate() {
        if (this._debounceTimeout) {
            clearTimeout(this._debounceTimeout);
            this._debounceTimeout = null;
        }
    }

    setCode(code: string) {
        if (this.editorView) {
            const transaction = this.editorView.state.update({
                changes: { from: 0, to: this.editorView.state.doc.length, insert: code }
            });
            this.editorView.dispatch(transaction);
        }
    }

    _saveCode(code: string) {
        try {
            if (this.options.storageKey) {
                localStorage.setItem(this.options.storageKey, code);
            }
        } catch (e: any) {
            console.warn("SWO: Unable to save to localStorage:", e.message);
        }
    }

    formatCode() {
        if (!this.editorView) return;

        const currentCode = this.editorView.state.doc.toString();
        const options: HTMLBeautifyOptions = {
            wrap_line_length: 120,
            indent_size: 2,
            indent_char: " ",
        };
        try {
            const formattedCode = html_beautify(currentCode, options);
            const transaction = this.editorView.state.update({
                changes: { from: 0, to: this.editorView.state.doc.length, insert: formattedCode }
            });
            this.editorView.dispatch(transaction);
        } catch (e) {
            console.error("SWO: Error during code formatting with js-beautify:", e);
            alert("Could not format the code. Check console for errors.");
        }
    }

    destroy() {
        this._disposed = true;
        this.cancelPendingUpdate();
        if (this.editorView) {
            this.editorView.destroy();
            this.editorView = null;
        }
    }

    _getDefaultInitialCode() {
        return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Live Preview</title>
    <style>
        body { font-family: sans-serif; background-color: #f0f0f0; color: #333; padding: 20px; margin: 0; }
        h1 { color: steelblue; }
        button { background-color: steelblue; color: white; padding: 10px 15px; border: none; border-radius: 5px; cursor: pointer; margin-top: 10px; }
        button:hover { background-color: darkslateblue; }
        .output { margin-top: 10px; padding: 10px; border: 1px solid #ccc; background-color: #fff; min-height: 20px; }
        .note { font-size: 0.9em; color: #555; margin-top:15px; }
    </style>
</head>
<body>
    <h1>Hello SenangWebs One!</h1>
    <p>This is a live preview. Edit the code on the left. Check the console for messages.</p>
    <button id="testBtn">Log Message</button>
    <button id="errorBtn">Log Error</button>
    <div id="outputDiv" class="output"></div>
    <p class="note">Open your browser's developer console to see original logs too.</p>
    <script>
        console.log("Iframe script loaded!", { a: 1, b: "text" });
        console.warn("This is a warning from iframe.");
        const testBtn = document.getElementById('testBtn');
        const errorBtn = document.getElementById('errorBtn');
        const outputDiv = document.getElementById('outputDiv');
        let clickCount = 0;
        if (testBtn) {
            testBtn.addEventListener('click', () => {
                clickCount++;
                const complexObject = { id: clickCount, timestamp: new Date(), nested: { data: [1,2,3] } };
                console.log("Button clicked:", clickCount, "times.", complexObject);
                if(outputDiv) outputDiv.textContent = "Logged to SWO console. Click: " + clickCount;
            });
        }
        if (errorBtn) {
            errorBtn.addEventListener('click', () => {
                console.error("This is a test error!", new Error("Something went wrong in iframe"));
            });
        }
    </script>
</body>
</html>`;
    }
}
